import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/ui';
import { getPlanDisplayName } from '@app/billing-config';
import { FadeIn, PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/billing')({
  validateSearch: z.object({
    status: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.subscriptions.queryOptions({
        input: { status: deps.status, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Billing,
});

function Billing() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data } = useSuspenseQuery(
    api.admin.subscriptions.queryOptions({
      input: { status: search.status, page: search.page },
    }),
  );

  const { subscriptions, total, page, totalPages, activeCount, estimatedMrr } = data;

  return (
    <PageTransition>
      <h1 className="text-2xl font-bold">Billing</h1>
      <p className="mt-2 text-muted-foreground">Subscription management and revenue tracking.</p>

      <StaggerContainer className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StaggerItem>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Subscriptions</p>
              <p className="mt-1 text-xl font-semibold">{total}</p>
            </CardContent>
          </Card>
        </StaggerItem>
        <StaggerItem>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="mt-1 text-xl font-semibold text-green-600">{activeCount}</p>
            </CardContent>
          </Card>
        </StaggerItem>
        <StaggerItem>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Estimated MRR</p>
              <p className="mt-1 text-xl font-semibold">{formatCurrency(estimatedMrr)}</p>
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>

      <div className="mt-4 flex gap-2">
        <Select
          value={search.status || 'all'}
          onValueChange={(v) =>
            navigate({ search: { status: v === 'all' ? undefined : v, page: 1 } })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="past_due">Past Due</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <FadeIn delay={0.3}>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Reference</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Period End</th>
                <th className="py-2">Auto-Renew</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">
                    No subscriptions found.
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4 font-mono text-xs">{sub.referenceId}</td>
                    <td className="py-2 pr-4">{getPlanDisplayName(sub.plan)}</td>
                    <td className="py-2 pr-4">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                          sub.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : sub.status === 'trialing'
                              ? 'bg-blue-100 text-blue-800'
                              : sub.status === 'canceled'
                                ? 'bg-muted text-muted-foreground'
                                : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {sub.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {sub.periodEnd ? new Date(sub.periodEnd).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2">{sub.cancelAtPeriodEnd ? 'No' : 'Yes'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}
